import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { LanguageContext } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, Calendar, ImageIcon } from "lucide-react";

const Home = () => {
  const { language } = useContext(LanguageContext);

  const content = {
    en: {
      heroTitle: "Explore Our Sacred Temple",
      heroSubtitle:
        "Welcome to the Sri Siddhachal Sthulabhadra Jain Teerth Dham Chandra hills, a spiritual haven dedicated to Jain Devotees. Immerse yourself in the rich history and culture of Jainism while experiencing the serenity and tranquility of our sacred grounds.",
      exploreBtn: "Explore Divine Attractions",
      divineTitle: "Divine Attractions",
      keyOfferings: "Key Offerings",
      offeringsSubtitle: "Experience our divine with ease",
      bhojanTitle: "Shrimati Pani hai Hastimalji Kothari Bhojanshala",
      bhojanDesc:
        "Bhojanshala is a sacred place where purity and devotion come together to offer wholesome and sattvic meals. Rooted in the principles of Jainism, our Bhojanshala provides nourishing food prepared with utmost care.",
      dharamTitle: "Sanghvi Bhavarlalji Pistadevi Bohra Dharmashala",
      dharamDesc:
        "A peaceful and comfortable stay for pilgrims and travelers. With 45 well-maintained rooms and 3 spacious halls, it provides an ideal space for spiritual retreats.",
      officeTitle: "Main Office of Tirth",
      officeDesc:
        "The central hub for all pilgrim services, inquiries, and management. It ensures the smooth operation of the Dharamshala, Bhojanshala, and other facilities.",
      moreDetails: "More Details",
      enquire: "Enquire Here",
      contact: "Contact",
      othersTitle: "Others",
      galleryTitle: "Photo Gallery",
      galleryDesc:
        "Explore the spiritual essence through divine moments, serene landscapes, and sacred rituals.",
      viewGallery: "View Gallery",
      eventsTitle: "Event Calendar",
      eventsDesc:
        "Stay updated with upcoming festivals, poojas, and events. Plan your visit and be part of spiritual celebrations.",
      viewEvents: "View Events",
      donationTitle: "Donation",
      donationDesc:
        "Support the Dharamshala, Bhojanshala, and temple services through your generous contributions.",
      donateNow: "Donate Now",
      testimonialTitle: "Your Experience",
      testimonialQuote: '"So proud of this temple and its architecture..."',
      testimonialAuthor: "Devotee",
    },
    hi: {
      heroTitle: "हमारे पवित्र मंदिर का अन्वेषण करें",
      heroSubtitle:
        "श्री सिद्धाचल स्थूलभद्र जैन तीर्थ धाम चंद्र हिल्स में आपका स्वागत है, जो जैन भक्तों के लिए समर्पित एक आध्यात्मिक स्थल है।",
      exploreBtn: "दिव्य आकर्षण देखें",
      divineTitle: "दिव्य आकर्षण",
      keyOfferings: "मुख्य सुविधाएँ",
      offeringsSubtitle: "हमारी दिव्य सुविधाओं का अनुभव करें",
      bhojanTitle: "श्रीमती पनि है हस्तिमलजी कोठारी भोजनशाला",
      bhojanDesc:
        "भोजनशाला एक पवित्र स्थान है जहाँ पवित्रता और भक्ति सात्त्विक भोजन प्रदान करने के लिए एक साथ आते हैं।",
      dharamTitle: "संघवी भावरलालजी पिस्तादेवी बोहरा धर्मशाला",
      dharamDesc:
        "तीर्थयात्रियों और यात्रियों के लिए एक शांतिपूर्ण और आरामदायक ठहराव। 45 अच्छी तरह से बनाए रखे गए कमरे और 3 विशाल हॉल के साथ।",
      officeTitle: "तीर्थ का मुख्य कार्यालय",
      officeDesc:
        "सभी तीर्थयात्री सेवाओं, पूछताछ और प्रबंधन के लिए केंद्रीय केंद्र।",
      moreDetails: "अधिक जानकारी",
      enquire: "यहाँ पूछताछ करें",
      contact: "संपर्क करें",
      othersTitle: "अन्य",
      galleryTitle: "फोटो गैलरी",
      galleryDesc:
        "दिव्य पलों, शांत भूदृश्यों और पवित्र अनुष्ठानों के माध्यम से आध्यात्मिक सार का अन्वेषण करें।",
      viewGallery: "गैलरी देखें",
      eventsTitle: "कार्यक्रम कैलेंडर",
      eventsDesc: "आगामी त्योहारों, पूजाओं और कार्यक्रमों के साथ अपडेट रहें।",
      viewEvents: "कार्यक्रम देखें",
      donationTitle: "दान",
      donationDesc:
        "अपने उदार योगदान के माध्यम से धर्मशाला, भोजनशाला और मंदिर सेवाओं का समर्थन करें।",
      donateNow: "अभी दान करें",
      testimonialTitle: "आपका अनुभव",
      testimonialQuote: '"इस मंदिर और इसकी वास्तुकला पर बहुत गर्व है..."',
      testimonialAuthor: "भक्त",
    },
  };

  const temples = [
    {
      id: "jay-taleti",
      name: "Sri Jay Taleti",
      nameHi: "श्री जय तलेटी",
      img: "https://static.wixstatic.com/media/4365ca_1258e0b08f274c79a07b3f2843068e73~mv2.png",
    },
    {
      id: "girnar-tunk",
      name: "Sri Girnar Tunk",
      nameHi: "श्री गिरनार टूंक",
      img: "https://static.wixstatic.com/media/4365ca_f45e65c17f3047e891a809c561130ace~mv2.png",
    },
    {
      id: "varshitap",
      name: "Sri Varshitap Mandir",
      nameHi: "श्री वर्षितप मंदिर",
      img: "https://static.wixstatic.com/media/4365ca_cbaff8dc125048b6b7d8cd248d5cba92~mv2.png",
    },
    {
      id: "adinath",
      name: "Sri Adinath Bhagwan Main Mandir",
      nameHi: "श्री आदिनाथ भगवान मुख्य मंदिर",
      img: "https://static.wixstatic.com/media/4365ca_bb83f89730b8417bb60bcea35f53f61e~mv2.png",
    },
    {
      id: "ghetipag",
      name: "Sri Ghetipag Mandir",
      nameHi: "श्री घेटीपाग मंदिर",
      img: "https://static.wixstatic.com/media/4365ca_16fd2e4343254440b2ad87cb161cc158~mv2.png",
    },
    {
      id: "labhdhi",
      name: "Sri Labhdhi Dadawadi",
      nameHi: "श्री लाभधि दाडावाडी",
      img: "https://static.wixstatic.com/media/4365ca_76c89b072465419b9ff41f8dcef34acb~mv2.png",
    },
  ];

  const t = content[language];

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="hero-section py-20 px-4" data-testid="hero-section">
        <div className="container mx-auto max-w-6xl text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold gradient-text mb-6"
            data-testid="hero-title"
          >
            {t.heroTitle}
          </h1>
          <p
            className="text-base md:text-lg text-amber-900 max-w-4xl mx-auto mb-8 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.heroSubtitle}
          </p>
          <Link to="/divine-attractions">
            <Button
              size="lg"
              className="btn-primary text-lg px-8 py-6"
              data-testid="explore-btn"
            >
              {t.exploreBtn} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Divine Attractions Section */}
      <section className="py-16 px-4 bg-white" data-testid="divine-section">
        <div className="container mx-auto max-w-7xl">
          <h2
            className="text-4xl sm:text-5xl font-bold text-center gradient-text mb-12"
            data-testid="divine-title"
          >
            {t.divineTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {temples.map((temple) => (
              <Link
                key={temple.id}
                to={`/temple/${temple.id}`}
                data-testid={`temple-card-${temple.id}`}
              >
                <div className="temple-card bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="h-64 overflow-hidden">
                    <img
                      src={temple.img}
                      alt={language === "en" ? temple.name : temple.nameHi}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-amber-900">
                      {language === "en" ? temple.name : temple.nameHi}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/divine-attractions">
              <Button
                size="lg"
                variant="outline"
                className="border-amber-700 text-amber-700 hover:bg-amber-50"
                data-testid="view-all-temples-btn"
              >
                {language === "en"
                  ? "View All Attractions"
                  : "सभी आकर्षण देखें"}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Offerings */}
      <section
        className="py-16 px-4 bg-gradient-to-br from-amber-50 to-orange-50"
        data-testid="offerings-section"
      >
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2
              className="text-4xl sm:text-5xl font-bold gradient-text mb-4"
              data-testid="offerings-title"
            >
              {t.keyOfferings}
            </h2>
            <p className="text-base text-amber-800">{t.offeringsSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              className="offering-card p-6 rounded-xl shadow-md"
              data-testid="offering-bhojan"
            >
              <h3 className="text-lg font-bold text-amber-900 mb-3">
                {t.bhojanTitle}
              </h3>
              <p className="text-sm text-amber-800 mb-4">{t.bhojanDesc}</p>
              <a
                href="https://16919ac6-3d0c-4c3c-a60b-6936f33a2d92.filesusr.com/ugd/4365ca_8e634564f7e9489bad637e434b8250a2.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-700 hover:bg-amber-50"
                >
                  {t.moreDetails}
                </Button>
              </a>
            </div>
            <div
              className="offering-card p-6 rounded-xl shadow-md"
              data-testid="offering-dharam"
            >
              <h3 className="text-lg font-bold text-amber-900 mb-3">
                {t.dharamTitle}
              </h3>
              <p className="text-sm text-amber-800 mb-4">{t.dharamDesc}</p>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScw02kBcEBR_Myyr_crsXlpBcWbzbUAml444CkXmmQruAafTg/viewform"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-700 hover:bg-amber-50"
                >
                  {t.enquire}
                </Button>
              </a>
            </div>
            <div
              className="offering-card p-6 rounded-xl shadow-md"
              data-testid="offering-office"
            >
              <h3 className="text-lg font-bold text-amber-900 mb-3">
                {t.officeTitle}
              </h3>
              <p className="text-sm text-amber-800 mb-4">{t.officeDesc}</p>
              <Link to="/contact">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-700 hover:bg-amber-50"
                >
                  {t.contact}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Others Section */}
      <section className="py-16 px-4 bg-white" data-testid="others-section">
        <div className="container mx-auto max-w-7xl">
          <h2
            className="text-4xl sm:text-5xl font-bold text-center gradient-text mb-12"
            data-testid="others-title"
          >
            {t.othersTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              className="glass-effect p-8 rounded-2xl text-center"
              data-testid="others-gallery"
            >
              <ImageIcon className="w-16 h-16 mx-auto mb-4 text-amber-700" />
              <h3 className="text-xl font-bold text-amber-900 mb-3">
                {t.galleryTitle}
              </h3>
              <p className="text-sm text-amber-800 mb-6">{t.galleryDesc}</p>
              <Link to="/gallery">
                <Button className="btn-primary">{t.viewGallery}</Button>
              </Link>
            </div>
            <div
              className="glass-effect p-8 rounded-2xl text-center"
              data-testid="others-events"
            >
              <Calendar className="w-16 h-16 mx-auto mb-4 text-amber-700" />
              <h3 className="text-xl font-bold text-amber-900 mb-3">
                {t.eventsTitle}
              </h3>
              <p className="text-sm text-amber-800 mb-6">{t.eventsDesc}</p>
              <Link to="/events">
                <Button className="btn-primary">{t.viewEvents}</Button>
              </Link>
            </div>
            <div
              className="glass-effect p-8 rounded-2xl text-center"
              data-testid="others-donation"
            >
              <Heart className="w-16 h-16 mx-auto mb-4 text-amber-700" />
              <h3 className="text-xl font-bold text-amber-900 mb-3">
                {t.donationTitle}
              </h3>
              <p className="text-sm text-amber-800 mb-6">{t.donationDesc}</p>
              <Link to="/donate">
                <Button className="btn-primary">{t.donateNow}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section
        className="py-16 px-4 bg-gradient-to-br from-amber-100 to-orange-100"
        data-testid="testimonial-section"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <h2
            className="text-4xl font-bold gradient-text mb-8"
            data-testid="testimonial-title"
          >
            {t.testimonialTitle}
          </h2>
          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <p
              className="text-xl md:text-2xl italic text-amber-900 mb-4"
              data-testid="testimonial-quote"
            >
              {t.testimonialQuote}
            </p>
            <p
              className="text-base font-semibold text-amber-700"
              data-testid="testimonial-author"
            >
              - {t.testimonialAuthor}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
